/* ==============================================================
   BITTERSWEET — store behaviour

   Ported from the single-file prototype. Two things changed in
   the move to Shopify, both because the store is now real:

   1. The hash router is gone. Shopify serves real URLs and real
      templates, so navigation is navigation.
   2. The cart is no longer an array in memory. Every mutation
      goes through the Cart AJAX API, and the drawer is re-rendered
      by Liquid via the Section Rendering API — which means money
      formatting, discounts and line properties stay server-side
      and correct rather than being approximated here.
   ============================================================== */
(() => {
  'use strict';

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const calm = matchMedia('(prefers-reduced-motion: reduce)');
  const root = document.documentElement;

  /* ---------- Seam: scroll feeds the thread through --------------- */
  let ticking = false;
  function seam() {
    const max = document.body.scrollHeight - innerHeight;
    root.style.setProperty('--seam', max > 0 ? (scrollY / max).toFixed(4) : 1);
    ticking = false;
  }
  addEventListener('scroll', () => {
    if (!ticking) { ticking = true; requestAnimationFrame(seam); }
  }, { passive: true });
  addEventListener('resize', seam, { passive: true });
  seam();

  /* ---------- Hero load choreography ------------------------------ */
  // Added after paint so the hero is legible even if this never runs. When the
  // intro is playing it is held back, so the hero performs its entrance at the
  // moment the store is actually revealed rather than behind the overlay.
  const playHero = () => {
    if (!calm.matches) requestAnimationFrame(() => document.body.classList.add('anim'));
  };
  if (root.dataset.intro === 'on') addEventListener('bsw:intro-done', playHero, { once: true });
  else playHero();

  /* ---------- Dialogs --------------------------------------------- */
  const cart = $('#cart');
  const menu = $('#menu');

  const openCart = () => cart && cart.showModal();

  $('#cartOpen')?.addEventListener('click', openCart);
  $('#menuOpen')?.addEventListener('click', () => menu && menu.showModal());

  // Delegated, because the drawer's contents are replaced on every cart change.
  document.addEventListener('click', e => {
    const close = e.target.closest('[data-close]');
    if (close) close.closest('dialog')?.close();
  });

  $$('dialog').forEach(d =>
    // Click on the backdrop (i.e. outside the panel) closes.
    d.addEventListener('click', e => { if (e.target === d) d.close(); })
  );
  $$('#menu nav a').forEach(a => a.addEventListener('click', () => menu.close()));

  /* ---------- Product gallery -------------------------------------- */
  // Thumbnails swap the main image. The main <img> keeps its own srcset so the
  // browser still picks a sensible size after the swap.
  const galleryMain = $('#galleryMain');
  if (galleryMain) {
    $$('.thumb').forEach(t => t.addEventListener('click', () => {
      $$('.thumb').forEach(o => o.setAttribute('aria-pressed', String(o === t)));
      if (t.dataset.src) galleryMain.src = t.dataset.src;
      if (t.dataset.srcset) galleryMain.srcset = t.dataset.srcset;
      galleryMain.alt = t.dataset.alt || '';
    }));
  }

  /* ---------- Variant selection ------------------------------------ */
  // Size radios carry their variant id, so picking a size updates the price,
  // the availability line and the submit button without a round trip.
  const buyForm = $('[data-buy]');

  // The variant control is whatever carries name="id" — radio chips for a
  // single-option product, a select for anything else. Both expose the chosen
  // element the same way, so the rest of this doesn't care which it is.
  function chosenVariant() {
    if (!buyForm) return null;
    const field = buyForm.elements.id;
    if (!field) return null;
    if (field instanceof HTMLSelectElement) return field.options[field.selectedIndex] || null;
    if (field instanceof RadioNodeList) return [...field].find(i => i.checked) || null;
    return field.checked === false ? null : field;
  }

  if (buyForm) {
    const submit = $('[data-add]', buyForm);
    const priceEl = $('[data-price]');
    const availEl = $('[data-availability]');

    const syncVariant = () => {
      const picked = chosenVariant();
      if (!picked) return;
      const available = picked.dataset.available === 'true';
      if (priceEl && picked.dataset.price) priceEl.textContent = picked.dataset.price;
      if (availEl) availEl.textContent = available ? '' : 'Sold out in this size.';
      submit.disabled = !available;
      submit.textContent = available ? 'Add to cart' : 'Sold out';
    };

    buyForm.addEventListener('change', e => {
      if (e.target.name === 'id') syncVariant();
    });
    syncVariant();
  }

  /* ---------- Cart -------------------------------------------------- */
  const DRAWER = 'bs-cart-drawer';

  // Taken from Liquid rather than hard-coded, so a store selling into more than
  // one market hits the cart on the right localised path.
  const ROOT = root.dataset.rootUrl || '/';
  const ADD_URL = (root.dataset.cartAddUrl || '/cart/add') + '.js';
  const CHANGE_URL = (root.dataset.cartChangeUrl || '/cart/change') + '.js';

  function confirmFor(form) {
    const said = $('[data-said]', form);
    if (!said) return;
    said.setAttribute('data-on', '');
    clearTimeout(said._t);
    said._t = setTimeout(() => said.removeAttribute('data-on'), 4000);
  }

  // Re-render the drawer from Liquid rather than rebuilding it here, so prices,
  // discounts and totals are formatted by the store, not guessed by the client.
  async function refreshCart() {
    const res = await fetch(`${ROOT}?sections=${DRAWER}`);
    if (!res.ok) return;
    const data = await res.json();
    const html = new DOMParser().parseFromString(data[DRAWER], 'text/html');

    const fresh = html.querySelector('[data-cart-content]');
    const live = $('[data-cart-content]');
    if (fresh && live) live.innerHTML = fresh.innerHTML;

    const count = html.querySelector('[data-cart-count]');
    $$('[data-cart-count]').forEach(el => {
      if (!count) return;
      el.textContent = count.textContent;
      el.dataset.empty = count.dataset.empty;
    });

    const n = Number(count?.textContent || 0);
    $('#cartOpen')?.setAttribute('aria-label', n ? `Open cart, ${n} item${n > 1 ? 's' : ''}` : 'Open cart');
  }

  function cartError(message) {
    const box = $('[data-cart-error]');
    if (!box) { alert(message); return; }
    box.textContent = message;
    box.hidden = false;
    clearTimeout(box._t);
    box._t = setTimeout(() => { box.hidden = true; }, 6000);
  }

  buyForm?.addEventListener('submit', async e => {
    e.preventDefault();
    const submit = $('[data-add]', buyForm);
    if (submit.disabled) return;

    const label = submit.textContent;
    submit.disabled = true;
    submit.textContent = 'Adding…';

    try {
      const res = await fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: chosenVariant().value, quantity: 1 }] })
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.description || body.message || 'That size could not be added.');

      await refreshCart();
      confirmFor(buyForm);
      openCart();
    } catch (err) {
      cartError(err.message);
    } finally {
      submit.disabled = false;
      submit.textContent = label;
    }
  });

  // Line changes, in the drawer and on the cart page. Delegated for the same
  // reason the close button is: this markup is replaced on every update.
  async function changeLine(key, quantity) {
    try {
      const res = await fetch(CHANGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!res.ok) throw new Error('The cart could not be updated.');
      const cartJson = await res.json();
      await refreshCart();
      // The cart page renders its lines server-side, so it needs a real reload.
      if ($('[data-cart-page]')) location.reload();
      return cartJson;
    } catch (err) {
      cartError(err.message);
    }
  }

  document.addEventListener('click', e => {
    const rm = e.target.closest('[data-remove]');
    if (rm) { e.preventDefault(); changeLine(rm.dataset.remove, 0); return; }

    const step = e.target.closest('[data-step]');
    if (step) {
      e.preventDefault();
      // getElementById, not $(`#${id}`) -- a cart line key is "variantId:propertiesHash",
      // and that colon breaks the string once it's parsed as a CSS selector.
      const input = document.getElementById(step.dataset.for);
      if (!input) return;
      const next = Math.max(0, Number(input.value) + Number(step.dataset.step));
      input.value = next;
      changeLine(input.dataset.key, next);
    }
  });

  document.addEventListener('change', e => {
    const input = e.target.closest('[data-qty-input]');
    if (input) changeLine(input.dataset.key, Math.max(0, Number(input.value)));
  });

  /* ---------- Forms -------------------------------------------------- */
  // The newsletter and contact forms are real Shopify forms and post normally.
  // Shopify returns to the page with a success param; the confirmation the
  // prototype faked is shown then, for real.
  const params = new URLSearchParams(location.search);
  if (params.has('customer_posted') || params.has('contact_posted')) {
    const form = params.has('customer_posted') ? $('[data-signup]') : $('[data-contact]');
    if (form) {
      confirmFor(form);
      form.scrollIntoView({ block: 'center', behavior: calm.matches ? 'auto' : 'smooth' });
    }
  }
})();
